import React from 'react';
import { Card, Badge, Button, Dropdown } from 'react-bootstrap';
import { 
  MapPin, 
  Phone, 
  User, 
  TrendingUp, 
  Calendar,
  Edit,
  Settings,
  Archive,
  MoreVertical,
  Eye,
  Palette
} from 'lucide-react';
import BoutiqueLogo from './BoutiqueLogo'; // ✅ Import du composant logo

const BoutiqueCard = ({ boutique, onEdit, onConfigure, onArchive, onView }) => {
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { variant: 'success', text: 'Active' },
      INACTIVE: { variant: 'warning', text: 'Inactive' },
      ARCHIVED: { variant: 'secondary', text: 'Archivée' },
      SUSPENDED: { variant: 'danger', text: 'Suspendue' }
    };
    
    const config = statusConfig[status] || statusConfig.ACTIVE;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Card 
      className="boutique-card h-100" 
      style={{ 
        border: `2px solid ${boutique.primary_color || '#3b82f6'}20`,
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* ✅ En-tête avec dégradé basé sur les couleurs de la boutique */}
      <div 
        className="card-header-gradient"
        style={{
          background: `linear-gradient(135deg, ${boutique.primary_color || '#3b82f6'}, ${boutique.secondaryColor || '#1d4ed8'})`,
          padding: '1rem',
          borderRadius: '10px 10px 0 0',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Effet de brillance */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.1))',
            pointerEvents: 'none'
          }}
        />

        <div className="d-flex justify-content-between align-items-start position-relative">
          {/* ✅ Logo et nom avec le composant BoutiqueLogo */}
          <div className="d-flex align-items-center">
            <div className="me-3">
              <BoutiqueLogo 
                boutique={boutique} 
                size="medium"
                showPlaceholder={true}
                style={{
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </div>
            
            <div>
              <h5 
                className="text-white mb-1"
                style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}
              >
                {boutique.name}
              </h5>
              {getStatusBadge(boutique.status)}
            </div>
          </div>
          
          {/* Menu actions */}
          <Dropdown align="end">
            <Dropdown.Toggle 
              variant="light" 
              size="sm" 
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <MoreVertical size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {onView && (
                <Dropdown.Item onClick={() => onView(boutique)}>
                  <Eye size={14} className="me-2" />
                  Voir détails
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => onConfigure(boutique)}>
                <Settings size={14} className="me-2" />
                Configuration
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onEdit(boutique)}>
                <Edit size={14} className="me-2" />
                Modifier
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item 
                onClick={() => onArchive(boutique.id)}
                className="text-danger"
              >
                <Archive size={14} className="me-2" />
                Archiver
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Card.Body className="p-3">
        {/* Informations principales */}
        <div className="boutique-info mb-3">
          {boutique.description && (
            <p className="text-muted small mb-2">
              {boutique.description.length > 80 
                ? `${boutique.description.substring(0, 80)}...` 
                : boutique.description
              }
            </p>
          )}
          
          {/* Gestionnaire */}
          {boutique.managerName && (
            <div className="d-flex align-items-center mb-2">
              <User size={16} className="text-muted me-2" />
              <div>
                <small className="text-muted">Gestionnaire</small>
                <div className="fw-medium">{boutique.managerName}</div>
              </div>
            </div>
          )}

          {/* Localisation */}
          {boutique.address && (
            <div className="d-flex align-items-center mb-2">
              <MapPin size={16} className="text-muted me-2" />
              <div>
                <small className="text-muted">Adresse</small>
                <div className="small">{boutique.address}</div>
              </div>
            </div>
          )}

          {/* Téléphone */}
          {boutique.phone && (
            <div className="d-flex align-items-center mb-2">
              <Phone size={16} className="text-muted me-2" />
              <div>
                <small className="text-muted">Téléphone</small>
                <div className="small">{boutique.phone}</div>
              </div>
            </div>
          )}
        </div>

        {/* ✅ Palette de couleurs */}
        <div className="color-section mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Palette size={16} className="text-muted me-2" />
              <small className="text-muted">Couleurs</small>
            </div>
            <div className="d-flex align-items-center">
              <div 
                className="color-preview me-1"
                style={{ 
                  width: '20px',
                  height: '20px',
                  backgroundColor: boutique.primary_color || '#3b82f6',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
                title={`Primaire: ${boutique.primary_color || '#3b82f6'}`}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              />
              <div 
                className="color-preview"
                style={{ 
                  width: '20px',
                  height: '20px',
                  backgroundColor: boutique.secondaryColor || '#1d4ed8',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
                title={`Secondaire: ${boutique.secondaryColor || '#1d4ed8'}`}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              />
            </div>
          </div>
          <div className="mt-1">
            <small className="text-muted">
              {boutique.primary_color || '#3b82f6'} • {boutique.secondary_color || '#1d4ed8'}
            </small>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="boutique-stats mb-3">
          <div className="row g-2">
            <div className="col-6">
              <div 
                className="stat-item text-center p-2"
                style={{
                  background: '#f8fafc',
                  borderRadius: '6px'
                }}
              >
                <div 
                  className="stat-value"
                  style={{
                    fontWeight: '700',
                    fontSize: '1.1rem',
                    color: '#1e293b'
                  }}
                >
                  {boutique.totalProducts || 0}
                </div>
                <small className="text-muted">Produits</small>
              </div>
            </div>
            <div className="col-6">
              <div 
                className="stat-item text-center p-2"
                style={{
                  background: '#f8fafc',
                  borderRadius: '6px'
                }}
              >
                <div 
                  className="stat-value"
                  style={{
                    fontWeight: '700',
                    fontSize: '1.1rem',
                    color: '#1e293b'
                  }}
                >
                  {boutique.totalUsers || 0}
                </div>
                <small className="text-muted">Utilisateurs</small>
              </div>
            </div>
          </div>
        </div>

        {/* Date de création */}
        {boutique.createdAt && (
          <div className="d-flex align-items-center pt-2 border-top">
            <Calendar size={14} className="text-muted me-2" />
            <small className="text-muted">
              Créée le {formatDate(boutique.createdAt)}
            </small>
          </div>
        )}
      </Card.Body>

      {/* ✅ Styles CSS intégrés */}
      <style jsx>{`
        .boutique-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </Card>
  );
};

export default BoutiqueCard;